import { Component, OnInit } from '@angular/core';
import { GeneralExerciseComponent } from '../../General/g-exercise.component';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from '../../shared/message.service';
import { TopicService } from '../topic.service';
import { TreeService } from '../../General/Tree/tree.service';
import { Graph } from '../../General/graph/lib/graph';
import { ColoredFlagRep } from './colored-flag-rep';
import { ColoredFlagNoRep } from './colored-flag-no-rep';
import { TreeWords } from './tree-words';
import { TreeNode } from '../../General/Tree/lib/tree-node';

@Component({
  selector: 'app-multiple-choice',
  templateUrl: './multiple-choice.component.html',
  styleUrls: ['./multiple-choice.component.css']
})
export class MultipleChoiceComponent extends GeneralExerciseComponent {

  answers: string[] = [];
  answerType: string = "checkbox";
  solution: Set<number> = new Set();
  checkedBox: boolean[];
  checkedRadio: number;
  error: number[];


  //Treefields
  private TYPE_WORD_TREE: string = "wordTree";
  private TYPE_COLOR_TREE: string = "colorTree";
  private TYPE_NO_TREE: string = "noTree";
  private treeService: TreeService;
  treeType: string = this.TYPE_NO_TREE;
  private graph: Graph;

  private canvas: HTMLCanvasElement = null;
  private readonly canvasID: string = 'graph-canvas';


  constructor(protected activatedRoute: ActivatedRoute, protected topicService: TopicService,
    protected messageService: MessageService, treeService: TreeService) {
    super(activatedRoute, topicService, messageService);
    this.treeService = treeService;
    this.printable = true;
  }


  isTree(): boolean {
    return this.treeType == this.TYPE_NO_TREE;
  }

  isNoTree(): boolean {
    return !this.isTree();
  }

  ngOnInit() {
    super.ngOnInit();
    window.onload = function () { };
    this.canvas = <HTMLCanvasElement>document.getElementById(this.canvasID); //Call this on ngOnInit(), the constructor can't reference it, since it hasn't been initialized
  }



  setTypeSpecificsFromConfiguration() {
    this.answers = this.configuration.typespecifics.answers;
    this.answerType = this.configuration.typespecifics.type;
    this.checkedBox = new Array(this.answers.length).fill(false);
    this.checkedRadio = undefined;
    this.canvas = <HTMLCanvasElement>document.getElementById(this.canvasID); //Call this on ngOnInit(), the constructor can't reference it, since it hasn't been initialized

    // build the solution out of the solution code
    let solutionCode: string = this.configuration.typespecifics.solutionCode;
    // get number of correct solutions
    let countCorrectAnswers: number = 0;
    let primes: number[] = [7, 11, 13, 17, 19];
    for (let prime of primes) {
      if (+solutionCode.charAt(prime) % 2 == 0) {
        countCorrectAnswers++;
      } else {
        break;
      }
    }
    // get IDs of correct solutions
    for (let p of this.createArray(countCorrectAnswers)) {
      this.solution.add(+solutionCode.charAt((p + 1) * (countCorrectAnswers)));
    }

    this.treeType = this.treeService.importTreeType(this.configuration);
    let hasRepetition = this.treeService.importHasRepetition(this.configuration);
    if (this.treeType == this.TYPE_NO_TREE) {
      this.canvas.style.visibility = "hidden";
    }

    if (this.treeType == this.TYPE_COLOR_TREE) {
      let depth = this.treeService.importTreeOld(this.configuration)[1];
      let alphabet = this.treeService.importTreeOld(this.configuration)[0];
      if (hasRepetition) {
        this.graph = new ColoredFlagRep(depth, alphabet, this.canvas);
        if (this.graph instanceof ColoredFlagRep) {
          this.graph.buildTree();
        }
      } else {
        this.graph = new ColoredFlagNoRep(depth, alphabet, this.canvas);
        if (this.graph instanceof ColoredFlagNoRep) {
          this.graph.buildTree();
        }
      }
      this.initColoredFlag();
    }
    else {
      let alphabet: [any[], number][] = this.treeService.importMultiTree(this.configuration);
      this.graph = new TreeWords(alphabet, hasRepetition, this.canvas);
      if (this.graph instanceof TreeWords) {
        this.graph.buildTree();
      }
      this.initWordTree();
    }
    if (this.graph != null)
      this.graph.update();
  }

  private initColoredFlag() {
    let hiddenContent: any[][] = this.treeService.importHiddenNodes(this.configuration);
    this.graph.getNodes().forEach(node => {
      if (node instanceof TreeNode) {
        let n: TreeNode = <TreeNode>node;
        let realContent = n.getContent();
        hiddenContent.forEach(content => {
          let hasSameContent: boolean = this.sameContent(realContent, content);
          if (hasSameContent) {
            n.setHidden(true);
          }
        })
      }
    })
    let deletedContent: any[][] = this.treeService.importDeletedNodes(this.configuration);
    this.graph.getNodes().forEach(node => {
      if (node instanceof TreeNode) {
        let n: TreeNode = <TreeNode>node;
        let realContent = n.getContent();
        deletedContent.forEach(content => {
          let hasSameContent: boolean = this.sameContent(realContent, content);
          if (hasSameContent) {
            if (this.graph instanceof ColoredFlagNoRep) {
              this.graph.deleteNode(n);
            }
            if (this.graph instanceof ColoredFlagRep) {
              this.graph.deleteNode(n);
            }
          }
        })
      }
    })
    let markedContent: any[][] = this.treeService.importMarkedNodes(this.configuration);
    this.graph.getNodes().forEach(node => {
      if (node instanceof TreeNode) {
        let n: TreeNode = <TreeNode>node;
        let realContent = n.getContent();
        markedContent.forEach(content => {
          let hasSameContent: boolean = this.sameContent(realContent, content);
          if (hasSameContent) {
            n.selected();
          }
        })
      }
    })
  }
  private initWordTree() {
    let subNodeContent: any[] = this.treeService.importSubNode(this.configuration);
    let hiddenContent: any[][] = this.treeService.importHiddenNodes(this.configuration);
    let deletedContent: any[][] = this.treeService.importDeletedNodes(this.configuration);
    let markedContent: any[][] = this.treeService.importMarkedNodes(this.configuration);


    if (this.graph instanceof TreeWords) {
      this.graph.setDeletedNodes(deletedContent);
      this.graph.setHiddenNodes(hiddenContent);
      this.graph.setMarkedNodes(markedContent);
      this.graph.setSubNode(subNodeContent);
    }
  }

  private sameContent(realContent: any[], content: any[]): boolean {
    let hasSameContent: boolean = true;
    hasSameContent = content.length == realContent.length;

    let i: number = 0;
    for (i = 0; i < content.length; i++) {
      hasSameContent = hasSameContent && (content[i] == realContent[i]);
    }
    return hasSameContent;
  }

  setTypeSpecificsFromState() {
    if (this.state.typespecific.checked != undefined) {
      this.checkedBox = this.state.typespecific.checked;
      this.checkedRadio = this.state.typespecific.checked;
    }


  }

  checkSolution() {
    let error = false;
    if (this.answerType == "checkbox") {
      for (let check in this.checkedBox) {
        if (this.checkedBox[check] != this.solution.has(+check)) {
          error = true;
        }
      }
    } else {
      if (!this.solution.has(this.checkedRadio)) {
        error = true;
      }
    }
    if (error) {
      this.messageService.addAlertWithTimeout(this.get("wrongAnswer"), "danger", 10000);
    } else {
      this.messageService.addAlertWithTimeout(this.get("correct"), "success", 3000);
      this.solved = true;
      this.state.solved = true;
      this.saveState();
    }
  }

  computeState() {
    if (this.answerType == "checkbox") {
      this.state.typespecific.checked = this.checkedBox;
    } else {
      this.state.typespecific.checked = this.checkedRadio;
    }
  }

  evaluate(): number[] {
    let result = 0;
    if (this.answerType == "checkbox") {
      for (let check in this.checkedBox) {
        if (this.checkedBox[check] == this.solution.has(+check)) {
          result++;
        }
      }
      return [result / this.answers.length, 1];
    } else {
      if (this.solution.has(this.checkedRadio)) {

        return [1, 1];
      }
    }
    return [0, 1];
  }




}
